import { SpeakingLevelBadge } from "../UI/SpeakingLevelBadge.tsx";
import { formatDateTime } from "../UI/Utils/formatDate.ts";
import { LoadingRows } from "../UI/Loading/LoadingRows.tsx";
import { Layout } from "../UI/Layout/Layout.tsx";
import { ScreenTitleBlock } from "../UI/Layout/ScreenTitleBlock.tsx";
import { Card } from "../UI/Card.tsx";
import { CenteredLoading } from "../UI/Loading/CenteredLoading.tsx";
import { useState, useMemo } from "react";
import { useLeaveTeacherCoordinatorReview } from "./TeacherCoordinatorReview/useLeaveTeacherCoordinatorReview.ts";
import { useUpdateLatestTeacherCoordinatorReview } from "./TeacherCoordinatorReview/useUpdateLatestTeacherCoordinatorReview.ts";
import { useTeacherIdParamsOrThrow } from "./useTeacherIdParamsOrThrow.ts";
import { useFranchiseeById } from "./useFranchiseeById.ts";
import { BreadCrumbsWrapper } from "../UI/Layout/BreadCrumbsWrapper.tsx";
import { BreadCrumbsItem } from "../UI/Layout/BreadCrumbsItem.tsx";
import { Navigate, useNavigate } from "react-router-dom";
import { AllFranchiseeScreenPath } from "./FranchiseeScreen.tsx";
import { useTeacherCoordinatorReviewHistory } from "./TeacherCoordinatorReview/useTeacherCoordinatorReviewHistory.ts";
import { useTeacherScore } from "./TeacherCoordinatorReview/useTeacherScore.ts";
import {
  teacherMissingReviewForPeriod,
  getCurrentPeriodStart,
} from "./TeacherCoordinatorReview/coordinatorReviewPeriod.ts";
import dayjs from "dayjs";
import { CoordinatorReviewScoreLegend } from "./TeacherCoordinatorReview/CoordinatorReviewScoreLegend.tsx";
import { useTeacherStudentReviews } from "./TeacherCoordinatorReview/useTeacherStudentReviews.ts";
import { useTeacherClassRooms } from "./TeacherAvailability/useTeacherClassRooms.ts";
import { ClassRoomDetailScreenPath } from "../ClassRoom/ClassRoomDetailScreen.tsx";
import { useUserGroupName } from "../UserGroup/useUserGroupName.ts";
import { TeacherScoreTrendCard } from "./TeacherCoordinatorReview/TeacherScoreTrendCard.tsx";
import { TeacherRankingSummary } from "./TeacherCoordinatorReview/TeacherRankingSummary.tsx";
import { TeacherSubScoreBreakdown } from "./TeacherCoordinatorReview/TeacherSubScoreBreakdown.tsx";
import { TeacherActivityCard } from "./TeacherActivity/TeacherActivityCard.tsx";
import { TeacherOnboardingLivesCard } from "../OnboardingLive/TeacherOnboardingLivesCard.tsx";
import { TeacherAssignmentCapCard } from "./TeacherAssignmentCap/TeacherAssignmentCapCard.tsx";
import {
  TEACHER_TIER_INFO,
  TeacherTier,
} from "./TeacherAssignmentCap/teacherTiers.ts";
import { useTeacherAssignmentCapOverview } from "./TeacherAssignmentCap/useTeacherAssignmentCapOverview.ts";
import {
  coordinatorWeightedScore,
  STUDENT_SCORE_WINDOW_MONTHS,
} from "./TeacherCoordinatorReview/teacherScoreAlgorithm.ts";
import {
  COORDINATOR_COLOR,
  STUDENT_COLOR,
} from "./TeacherCoordinatorReview/teacherScoreColors.ts";

export const TeacherDetailScreenPath = "/teachers/:teacherId";
export const LegacyTeacherDetailScreenPath =
  "/teacher-coordinator-reviews/:teacherId";

export function RedirectToTeacherDetailScreen() {
  const { teacherId } = useTeacherIdParamsOrThrow();
  return (
    <Navigate
      to={TeacherDetailScreenPath.replace(":teacherId", teacherId)}
      replace
    />
  );
}

type ScoreInputProps = {
  label: string;
  value: number;
  onChange: (v: number) => void;
};

function ScoreInput({ label, value, onChange }: ScoreInputProps) {
  return (
    <div className={"flex items-center gap-3"}>
      <span className={"text-sm text-slate-600 w-36"}>{label}</span>
      <div className={"flex gap-1"}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type={"button"}
            onClick={() => onChange(n)}
            className={[
              "w-9 h-9 rounded border text-sm font-medium transition-colors",
              value === n
                ? "bg-slate-800 text-white border-slate-800"
                : "bg-white text-slate-600 border-slate-300 hover:border-slate-500",
            ].join(" ")}
          >
            {n}
          </button>
        ))}
      </div>
    </div>
  );
}

function FinalScoreBadge({
  value,
  hasCoordinatorScore,
  tier,
}: {
  value: number;
  hasCoordinatorScore: boolean;
  tier: TeacherTier | null;
}) {
  const color =
    value >= 4
      ? "bg-green-100 text-green-800"
      : value >= 3
        ? "bg-yellow-100 text-yellow-800"
        : "bg-red-100 text-red-800";
  return (
    <div className={"flex flex-col items-start gap-1.5"}>
      <span className={"text-xs font-medium text-slate-500"}>Final score</span>
      <div
        className={`flex flex-col items-center px-5 py-2.5 rounded-2xl ${color}`}
      >
        <span className={"text-4xl font-bold"}>{value.toFixed(2)}</span>
        {tier && (
          <span className={"text-xs font-semibold"}>
            {TEACHER_TIER_INFO[tier].label}
          </span>
        )}
      </div>
      <span className={"text-xs text-slate-400"}>
        {hasCoordinatorScore
          ? "Average of student and coordinator score"
          : "Equals the student score until a coordinator review"}
      </span>
    </div>
  );
}

function ContributingScore({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: string;
}) {
  return (
    <div className={"flex flex-col gap-1"}>
      <span className={"flex items-center gap-1.5 text-xs text-slate-500"}>
        <span
          className={"w-2 h-2 rounded-full"}
          style={{ backgroundColor: color }}
        />
        {label}
      </span>
      <span className={"text-2xl font-semibold text-slate-700"}>
        {value.toFixed(2)}
      </span>
    </div>
  );
}

function StudentReviewGroup({
  student,
}: {
  student: import("./TeacherCoordinatorReview/useTeacherStudentReviews.ts").StudentReviewGroup;
}) {
  const [expanded, setExpanded] = useState(false);
  const avgColor =
    student.decayedAvg >= 4
      ? "bg-green-100 text-green-700"
      : student.decayedAvg >= 3
        ? "bg-yellow-100 text-yellow-700"
        : "bg-red-100 text-red-700";

  return (
    <div className={"border border-slate-100 rounded-lg"}>
      <button
        type={"button"}
        onClick={() => setExpanded((p) => !p)}
        className={
          "w-full flex items-center justify-between px-4 py-3 text-left"
        }
      >
        <div className={"flex items-center gap-3"}>
          <button
            type={"button"}
            onClick={(e) => {
              e.stopPropagation();
              navigator.clipboard.writeText(student.userId);
            }}
            title={"Copy user ID"}
            className={
              "text-xs text-slate-400 font-mono bg-slate-50 border border-slate-200 px-2 py-0.5 rounded hover:bg-slate-100 hover:text-slate-600 transition-colors"
            }
          >
            {student.userId}
          </button>
          <span className={"text-xs text-slate-400"}>
            {student.reviews.length}{" "}
            {student.reviews.length === 1 ? "review" : "reviews"}
          </span>
        </div>
        <div className={"flex items-center gap-3"}>
          <span
            className={`text-sm font-semibold px-3 py-0.5 rounded-full ${avgColor}`}
          >
            {student.decayedAvg.toFixed(2)}
          </span>
          <span className={"text-slate-400 text-xs"}>
            {expanded ? "▲" : "▼"}
          </span>
        </div>
      </button>

      {expanded && (
        <div
          className={"border-t border-slate-100 divide-y divide-slate-50 px-4"}
        >
          {student.reviews.map((review) => {
            const weightPct = Math.round(review.decayWeight * 100);
            const itemColor =
              review.teacherAvg >= 4
                ? "text-green-700"
                : review.teacherAvg >= 3
                  ? "text-yellow-700"
                  : "text-red-700";
            return (
              <div
                key={review.id}
                className={"flex items-center justify-between py-2.5"}
              >
                <div className={"flex flex-col gap-0.5"}>
                  <span className={"text-sm text-slate-700"}>
                    {dayjs(review.reviewedAtUtc).format("D MMM YYYY")}
                  </span>
                  <span className={"text-xs text-slate-400"}>
                    Clearance {review.clearanceScore} · Involvement{" "}
                    {review.involvementScore} · Comfortable{" "}
                    {review.comfortableScore}
                  </span>
                </div>
                <div className={"flex items-center gap-3"}>
                  <span
                    className={
                      "text-xs text-slate-400 bg-slate-50 px-2 py-0.5 rounded"
                    }
                  >
                    weight {weightPct}%
                  </span>
                  <span
                    className={`text-sm font-semibold w-8 text-right ${itemColor}`}
                  >
                    {review.teacherAvg.toFixed(1)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function TeacherDetailScreen() {
  const { teacherId } = useTeacherIdParamsOrThrow();
  const { franchisee, loading: loadingTeacher } = useFranchiseeById(teacherId);
  const {
    history,
    loading: loadingHistory,
    refetch: refetchHistory,
  } = useTeacherCoordinatorReviewHistory(teacherId);
  const { score, loading: loadingScore } = useTeacherScore(teacherId);
  const { overview: assignmentCapOverview } =
    useTeacherAssignmentCapOverview(teacherId);
  const { leaveReview, loading: saving } = useLeaveTeacherCoordinatorReview();
  const { updateReview, loading: updating } =
    useUpdateLatestTeacherCoordinatorReview();
  const navigate = useNavigate();

  const [fluencyScore, setFluencyScore] = useState(0);
  const [presenceScore, setPresenceScore] = useState(0);
  const [clarityScore, setClarityScore] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [studentReviewsExpanded, setStudentReviewsExpanded] = useState(false);
  const [lessonsExpanded, setLessonsExpanded] = useState(false);

  const defaultEndDate = dayjs().format("YYYY-MM-DD");
  const defaultStartDate = dayjs()
    .subtract(STUDENT_SCORE_WINDOW_MONTHS, "month")
    .format("YYYY-MM-DD");
  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);

  const { result: studentReviews, loading: loadingStudentReviews } =
    useTeacherStudentReviews(teacherId, startDate, endDate);

  const { classRooms, loading: loadingClassRooms } = useTeacherClassRooms({
    teacherId,
    fromDate: dayjs(startDate),
    toDate: dayjs(endDate),
  });
  const getGroupName = useUserGroupName();

  const scoreColor = useMemo(() => {
    const v = studentReviews?.simulatedStudentScore ?? 0;
    return v >= 4
      ? "bg-green-100 text-green-800"
      : v >= 3
        ? "bg-yellow-100 text-yellow-800"
        : "bg-red-100 text-red-800";
  }, [studentReviews?.simulatedStudentScore]);

  const latestReview = history[0] ?? null;
  const periodStart = getCurrentPeriodStart();
  const needsReview = teacherMissingReviewForPeriod(
    franchisee?.createdAt,
    latestReview?.reviewedAtUtc,
  );
  const currentPeriodReview = latestReview
    ? new Date(latestReview.reviewedAtUtc) >= periodStart
      ? latestReview
      : null
    : null;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (fluencyScore === 0 || presenceScore === 0 || clarityScore === 0) return;
    await leaveReview({
      teacherId,
      fluencyScore,
      presenceScore,
      clarityScore,
    });
    setFluencyScore(0);
    setPresenceScore(0);
    setClarityScore(0);
    await refetchHistory();
  }

  const loading = loadingTeacher || loadingHistory || loadingScore;

  return (
    <Layout>
      <Card>
        <ScreenTitleBlock
          breadCrumbs={
            <BreadCrumbsWrapper>
              <BreadCrumbsItem
                onClick={() => navigate(AllFranchiseeScreenPath)}
                title={"Teachers"}
              />
              <BreadCrumbsItem
                title={
                  franchisee
                    ? `${franchisee.givenName} ${franchisee.familyName}`
                    : "..."
                }
              />
            </BreadCrumbsWrapper>
          }
          title={"Overview"}
        />
      </Card>

      {loading ? (
        <div className={"mt-4"}>
          <CenteredLoading />
        </div>
      ) : !franchisee ? null : (
        <div className={"mt-4 flex flex-col gap-4"}>
          {/* Score section */}
          <Card>
            <h2
              className={
                "text-sm font-semibold text-slate-500 uppercase tracking-wide mb-4"
              }
            >
              Current Score
            </h2>
            {score ? (
              <div className={"flex flex-col gap-6"}>
                <div
                  className={
                    "flex flex-wrap items-center justify-between gap-8"
                  }
                >
                  <div className={"flex flex-wrap items-center gap-8"}>
                    <FinalScoreBadge
                      value={score.finalScore}
                      hasCoordinatorScore={score.coordinatorScore !== null}
                      tier={assignmentCapOverview?.tier ?? null}
                    />
                    <div
                      className={
                        "flex items-center gap-8 border-l border-slate-100 pl-8"
                      }
                    >
                      <ContributingScore
                        label={"Student score"}
                        value={score.studentBayesianAvg}
                        color={STUDENT_COLOR}
                      />
                      {score.coordinatorScore !== null && (
                        <ContributingScore
                          label={"Coordinator score"}
                          value={score.coordinatorScore}
                          color={COORDINATOR_COLOR}
                        />
                      )}
                    </div>
                  </div>
                  <TeacherRankingSummary teacherId={teacherId} />
                </div>
                <div className={"border-t border-slate-100 pt-6"}>
                  <TeacherSubScoreBreakdown
                    teacherId={teacherId}
                    coordinatorReviews={history}
                  />
                </div>
              </div>
            ) : (
              <p className={"text-sm text-slate-400"}>No data yet.</p>
            )}
          </Card>

          <TeacherScoreTrendCard
            teacherId={teacherId}
            coordinatorReviews={history}
          />

          <TeacherAssignmentCapCard
            teacherId={teacherId}
            isInternalTeacher={!!franchisee.internalTeacher}
          />

          <TeacherActivityCard
            teacherId={teacherId}
            isInternalTeacher={!!franchisee.internalTeacher}
          />

          <TeacherOnboardingLivesCard teacherId={teacherId} />

          {/* Review form — only if missing for current period */}
          {needsReview ? (
            <Card>
              <h2
                className={
                  "text-sm font-semibold text-slate-500 uppercase tracking-wide mb-4"
                }
              >
                New Review — Period started{" "}
                {dayjs(periodStart).format("D MMM YYYY")}
              </h2>
              <div
                className={
                  "mb-6 p-4 bg-slate-50 rounded-lg border border-slate-200"
                }
              >
                <p
                  className={
                    "text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3"
                  }
                >
                  Score reference
                </p>
                <CoordinatorReviewScoreLegend />
              </div>
              <form onSubmit={handleSubmit} className={"flex flex-col gap-4"}>
                <ScoreInput
                  label={"Fluency conditions"}
                  value={fluencyScore}
                  onChange={setFluencyScore}
                />
                <ScoreInput
                  label={"Role adaptation"}
                  value={presenceScore}
                  onChange={setPresenceScore}
                />
                <ScoreInput
                  label={"Clarity"}
                  value={clarityScore}
                  onChange={setClarityScore}
                />
                <div>
                  <button
                    type={"submit"}
                    disabled={
                      saving ||
                      fluencyScore === 0 ||
                      presenceScore === 0 ||
                      clarityScore === 0
                    }
                    className={
                      "px-4 py-2 bg-slate-800 text-white text-sm rounded disabled:opacity-50"
                    }
                  >
                    {saving ? "Saving..." : "Save review"}
                  </button>
                </div>
              </form>
            </Card>
          ) : currentPeriodReview ? (
            <Card>
              {isEditing ? (
                <>
                  <h2
                    className={
                      "text-sm font-semibold text-slate-500 uppercase tracking-wide mb-4"
                    }
                  >
                    Edit Review —{" "}
                    {dayjs(currentPeriodReview.reviewedAtUtc).format(
                      "D MMM YYYY",
                    )}
                  </h2>
                  <div
                    className={
                      "mb-6 p-4 bg-slate-50 rounded-lg border border-slate-200"
                    }
                  >
                    <p
                      className={
                        "text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3"
                      }
                    >
                      Score reference
                    </p>
                    <CoordinatorReviewScoreLegend />
                  </div>
                  <form
                    onSubmit={async (e) => {
                      e.preventDefault();
                      if (
                        fluencyScore === 0 ||
                        presenceScore === 0 ||
                        clarityScore === 0
                      )
                        return;
                      await updateReview({
                        teacherId,
                        fluencyScore,
                        presenceScore,
                        clarityScore,
                      });
                      setFluencyScore(0);
                      setPresenceScore(0);
                      setClarityScore(0);
                      setIsEditing(false);
                      await refetchHistory();
                    }}
                    className={"flex flex-col gap-4"}
                  >
                    <ScoreInput
                      label={"Fluency conditions"}
                      value={fluencyScore}
                      onChange={setFluencyScore}
                    />
                    <ScoreInput
                      label={"Role adaptation"}
                      value={presenceScore}
                      onChange={setPresenceScore}
                    />
                    <ScoreInput
                      label={"Clarity"}
                      value={clarityScore}
                      onChange={setClarityScore}
                    />
                    <div className={"flex gap-2"}>
                      <button
                        type={"submit"}
                        disabled={
                          updating ||
                          fluencyScore === 0 ||
                          presenceScore === 0 ||
                          clarityScore === 0
                        }
                        className={
                          "px-4 py-2 bg-slate-800 text-white text-sm rounded disabled:opacity-50"
                        }
                      >
                        {updating ? "Saving..." : "Save changes"}
                      </button>
                      <button
                        type={"button"}
                        onClick={() => {
                          setFluencyScore(0);
                          setPresenceScore(0);
                          setClarityScore(0);
                          setIsEditing(false);
                        }}
                        className={
                          "px-4 py-2 text-slate-600 text-sm rounded border border-slate-300 hover:border-slate-500"
                        }
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </>
              ) : (
                <div className={"flex items-center justify-between"}>
                  <div className={"flex items-center gap-2"}>
                    <span className={"text-green-600 font-medium text-sm"}>
                      ✓ Review submitted for this period
                    </span>
                    <span className={"text-slate-400 text-sm"}>
                      (
                      {dayjs(currentPeriodReview.reviewedAtUtc).format(
                        "D MMM YYYY",
                      )}
                      )
                    </span>
                  </div>
                  <button
                    type={"button"}
                    onClick={() => {
                      setFluencyScore(currentPeriodReview.scores.fluencyScore);
                      setPresenceScore(
                        currentPeriodReview.scores.presenceScore,
                      );
                      setClarityScore(currentPeriodReview.scores.clarityScore);
                      setIsEditing(true);
                    }}
                    className={
                      "text-sm text-slate-500 underline hover:text-slate-800"
                    }
                  >
                    Edit
                  </button>
                </div>
              )}
            </Card>
          ) : null}

          {/* Shared period selector */}
          <Card>
            <h2
              className={
                "text-sm font-semibold text-slate-500 uppercase tracking-wide mb-4"
              }
            >
              Period
            </h2>
            <div className={"flex items-center gap-2"}>
              <label className={"text-xs text-slate-500"}>From</label>
              <input
                type={"date"}
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className={
                  "text-sm border border-slate-300 rounded px-2 py-1 focus:outline-none focus:border-slate-500"
                }
              />
              <label className={"text-xs text-slate-500"}>to</label>
              <input
                type={"date"}
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className={
                  "text-sm border border-slate-300 rounded px-2 py-1 focus:outline-none focus:border-slate-500"
                }
              />
            </div>
          </Card>

          {/* Student reviews */}
          <Card>
            <button
              type={"button"}
              onClick={() => setStudentReviewsExpanded((p) => !p)}
              className={"w-full flex items-center justify-between mb-4"}
            >
              <h2
                className={
                  "text-sm font-semibold text-slate-500 uppercase tracking-wide"
                }
              >
                Student Reviews
              </h2>
              <span className={"text-slate-400 text-xs"}>
                {studentReviewsExpanded ? "▲" : "▼"}
              </span>
            </button>

            {/* Simulated score — always visible */}
            <div className={"flex flex-wrap items-end gap-4 mb-6"}>
              {studentReviews &&
                (() => {
                  const studentSim = studentReviews.simulatedStudentScore;
                  const coordScore = score?.coordinatorScore ?? null;
                  const finalSim =
                    coordScore !== null
                      ? (studentSim + coordScore) / 2
                      : studentSim;
                  const finalColor =
                    finalSim >= 4
                      ? "bg-green-100 text-green-800"
                      : finalSim >= 3
                        ? "bg-yellow-100 text-yellow-800"
                        : "bg-red-100 text-red-800";
                  const totalReviews = studentReviews.students.reduce(
                    (sum, s) => sum + s.reviews.length,
                    0,
                  );
                  return (
                    <div className={"flex flex-wrap items-center gap-4"}>
                      <div className={"flex items-center gap-2"}>
                        <span className={"text-xs text-slate-500"}>
                          Reviews:
                        </span>
                        <span
                          className={
                            "text-sm font-bold px-3 py-1 rounded-lg bg-slate-100 text-slate-700"
                          }
                        >
                          {totalReviews}
                        </span>
                      </div>
                      <div className={"flex items-center gap-2"}>
                        <span className={"text-xs text-slate-500"}>
                          Student score:
                        </span>
                        <span
                          className={`text-sm font-bold px-3 py-1 rounded-lg ${scoreColor}`}
                        >
                          {studentSim.toFixed(2)}
                        </span>
                      </div>
                      {coordScore !== null && (
                        <div className={"flex items-center gap-2"}>
                          <span className={"text-xs text-slate-500"}>
                            Coordinator score:
                          </span>
                          <span
                            className={
                              "text-sm font-bold px-3 py-1 rounded-lg bg-slate-100 text-slate-700"
                            }
                          >
                            {coordScore.toFixed(2)}
                          </span>
                        </div>
                      )}
                      <div className={"flex items-center gap-2"}>
                        <span className={"text-xs text-slate-500"}>
                          Simulated final:
                        </span>
                        <span
                          className={`text-sm font-bold px-3 py-1 rounded-lg ${finalColor}`}
                        >
                          {finalSim.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  );
                })()}
            </div>

            {studentReviewsExpanded &&
              (loadingStudentReviews ? (
                <LoadingRows rows={4} />
              ) : !studentReviews || studentReviews.students.length === 0 ? (
                <p className={"text-sm text-slate-400"}>
                  No student reviews in this period.
                </p>
              ) : (
                <div className={"flex flex-col gap-4"}>
                  {studentReviews.students.map((student) => (
                    <StudentReviewGroup
                      key={student.userId}
                      student={student}
                    />
                  ))}
                </div>
              ))}
          </Card>

          {/* Teacher Coordinator Reviews */}
          <Card>
            <button
              type={"button"}
              onClick={() => setHistoryExpanded((p) => !p)}
              className={"w-full flex items-center justify-between"}
            >
              <h2
                className={
                  "text-sm font-semibold text-slate-500 uppercase tracking-wide"
                }
              >
                Teacher Coordinator Reviews
              </h2>
              <span className={"text-slate-400 text-xs"}>
                {historyExpanded ? "▲" : "▼"}
              </span>
            </button>
            {historyExpanded && (
              <div className={"mt-4"}>
                {history.length === 0 ? (
                  <p className={"text-sm text-slate-400"}>No reviews yet.</p>
                ) : (
                  <div className={"flex flex-col divide-y divide-slate-100"}>
                    {history.map((review) => {
                      const avg = coordinatorWeightedScore(review.scores);
                      return (
                        <div
                          key={review.id}
                          className={"flex items-center justify-between py-3"}
                        >
                          <div className={"flex flex-col gap-1"}>
                            <span
                              className={"text-sm font-medium text-slate-700"}
                            >
                              {dayjs(review.reviewedAtUtc).format("D MMM YYYY")}
                            </span>
                            <span className={"text-xs text-slate-400"}>
                              Fluency {review.scores.fluencyScore} · Presence{" "}
                              {review.scores.presenceScore} · Clarity{" "}
                              {review.scores.clarityScore}
                            </span>
                          </div>
                          <span
                            className={[
                              "text-sm font-semibold px-3 py-1 rounded-full",
                              avg >= 4
                                ? "bg-green-100 text-green-700"
                                : avg >= 3
                                  ? "bg-yellow-100 text-yellow-700"
                                  : "bg-red-100 text-red-700",
                            ].join(" ")}
                          >
                            {avg.toFixed(2)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </Card>

          {/* Classrooms */}
          <Card>
            <button
              type={"button"}
              onClick={() => setLessonsExpanded((p) => !p)}
              className={"w-full flex items-center justify-between"}
            >
              <h2
                className={
                  "text-sm font-semibold text-slate-500 uppercase tracking-wide"
                }
              >
                Classrooms
              </h2>
              <div className={"flex items-center gap-3"}>
                {!loadingClassRooms && (
                  <span
                    className={
                      "text-sm font-bold px-3 py-1 rounded-lg bg-slate-100 text-slate-700"
                    }
                  >
                    {classRooms.length}
                  </span>
                )}
                <span className={"text-slate-400 text-xs"}>
                  {lessonsExpanded ? "▲" : "▼"}
                </span>
              </div>
            </button>
            {lessonsExpanded && (
              <div className={"mt-4"}>
                {loadingClassRooms ? (
                  <LoadingRows rows={4} />
                ) : classRooms.length === 0 ? (
                  <p className={"text-sm text-slate-400"}>
                    No classrooms in this period.
                  </p>
                ) : (
                  <div className={"flex flex-col divide-y divide-slate-100"}>
                    {[...classRooms]
                      .sort(
                        (a, b) =>
                          new Date(b.startedAtUtc).getTime() -
                          new Date(a.startedAtUtc).getTime(),
                      )
                      .map((cr) => (
                        <div
                          key={cr.id}
                          className={"flex items-center justify-between py-3"}
                        >
                          <div className={"flex flex-col gap-0.5"}>
                            <span
                              className={"text-sm font-medium text-slate-700"}
                            >
                              {formatDateTime(cr.startedAtUtc)}
                            </span>
                            <span className={"text-xs text-slate-400"}>
                              {cr.is1on1 ? "1-to-1" : "Group"} ·{" "}
                              {getGroupName(cr.userGroupId)}
                              {!cr.is1on1 && <> · {cr.topic?.name ?? "—"}</>} ·
                              <SpeakingLevelBadge
                                level={cr.speakingLevel}
                              /> · {cr.participantsCount}{" "}
                              {cr.participantsCount === 1
                                ? "participant"
                                : "participants"}
                            </span>
                          </div>
                          <button
                            type={"button"}
                            onClick={() =>
                              navigate(
                                ClassRoomDetailScreenPath.replace(
                                  ":classRoomId",
                                  cr.id,
                                ),
                              )
                            }
                            className={
                              "text-sm text-slate-500 underline hover:text-slate-800"
                            }
                          >
                            Details
                          </button>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            )}
          </Card>
        </div>
      )}
    </Layout>
  );
}
